import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Stack,
    Avatar,
} from "@mui/material";
import AsyncButton from "../../components/common/AsyncButton";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { InvokeAPI } from "../../apis/API";
import { getAiMgmtBase } from "../../config/apiBase";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { isTrial } from "../../contexts/AuthContext";

/** Markdown styling for tutor bubbles (compact for chat width). */
const assistantMarkdownSx = {
    fontSize: "0.875rem",
    lineHeight: 1.55,
    wordBreak: "break-word",
    "& p": {
        margin: "0.35em 0",
        "&:first-of-type": { marginTop: 0 },
        "&:last-of-type": { marginBottom: 0 },
    },
    "& ul, & ol": { paddingLeft: "1.25rem", margin: "0.35em 0" },
    "& li": { marginBottom: "0.2em" },
    "& strong": { fontWeight: 600 },
    "& a": { color: "primary.main", wordBreak: "break-word" },
    "& h1, & h2, & h3, & h4": { fontWeight: 600, margin: "0.4em 0 0.2em", fontSize: "0.95rem" },
    "& code": {
        fontFamily: "ui-monospace, monospace",
        backgroundColor: "action.hover",
        padding: "0.1em 0.35em",
        borderRadius: 0.5,
        fontSize: "0.8125rem",
    },
    "& pre": {
        backgroundColor: "action.hover",
        padding: "0.75rem",
        borderRadius: 1,
        overflow: "auto",
        margin: "0.35em 0",
        maxWidth: "100%",
        "& code": { backgroundColor: "transparent", padding: 0 },
    },
    "& table": {
        width: "100%",
        borderCollapse: "collapse",
        margin: "0.35em 0",
        display: "block",
        overflowX: "auto",
    },
    "& th, & td": {
        border: "1px solid",
        borderColor: "divider",
        padding: "0.35em 0.5em",
        fontSize: "0.8125rem",
        verticalAlign: "top",
    },
    "& th": { fontWeight: 600, backgroundColor: "action.hover" },
    "& blockquote": {
        borderLeft: "3px solid",
        borderColor: "primary.main",
        margin: "0.35em 0",
        paddingLeft: "0.75rem",
        opacity: 0.95,
    },
    "& hr": { borderColor: "divider", margin: "0.75em 0" },
};
const MAX_MESSAGE_LENGTH = 500;

const DEFAULT_TUTOR_WELCOME =
    "I am your AI assistant tutor, please feel free to ask me your queries on this question.";
const TRIAL_ACCOUNT_NOTICE =
    "This feature is enabled for activated account only. Please activate your account.";

/** Typing-indicator dots (assistant message pending). */
function ChatTypingIndicator() {
    const dot = {
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: "text.secondary",
        "@keyframes chatTypingBounce": {
            "0%, 80%, 100%": { transform: "scale(0.85)", opacity: 0.35 },
            "40%": { transform: "scale(1)", opacity: 1 },
        },
        animation: "chatTypingBounce 1.05s ease-in-out infinite",
    };
    return (
        <Box
            role="status"
            aria-label="Tutor is typing"
            sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.25, minHeight: 22 }}
        >
            <Box component="span" sx={{ ...dot, animationDelay: "0s" }} />
            <Box component="span" sx={{ ...dot, animationDelay: "0.2s" }} />
            <Box component="span" sx={{ ...dot, animationDelay: "0.4s" }} />
        </Box>
    );
}

export function plainQuestionText(question) {
    if (question == null) {
        return "";
    }
    if (typeof question === "string" || typeof question === "number") {
        return String(question);
    }
    return "";
}

export function AssessmentQuestionChatDialog({
    open,
    onClose,
    studentUserId,
    assessmentId,
    questionId,
    questionNumber,
    questionText,
    token,
    onAuthFailure,
}) {
    const isTrialAccount = isTrial();
    const [sessionId, setSessionId] = useState("");
    const sessionIdRef = useRef("");
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [starting, setStarting] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [isExpanded, setIsExpanded] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        sessionIdRef.current = sessionId;
    }, [sessionId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, sending]);

    useEffect(() => {
        if (!open) {
            setIsExpanded(false);
        }
    }, [open]);

    // Tell the server to drop in-memory chat state when the dialog is dismissed (X, Close, Escape).
    useEffect(() => {
        if (open) {
            return undefined;
        }
        const sid = sessionIdRef.current;
        if (!sid) {
            return undefined;
        }
        sessionIdRef.current = "";
        setSessionId("");
        (async () => {
            await InvokeAPI(
                `${getAiMgmtBase()}/v1/ai/chat/session/close`,
                { SessionID: sid },
                "POST",
                { authorization: token },
                false
            );
        })();
        return undefined;
    }, [open, token]);

    useEffect(() => {
        if (!open || !studentUserId || !assessmentId || !questionId || isTrialAccount) {
            setStarting(false);
            if (isTrialAccount) {
                setSessionId("");
                setMessages([]);
                setInput("");
                setError("");
            }
            return undefined;
        }
        let cancelled = false;
        setSessionId("");
        setMessages([{ role: "assistant", text: DEFAULT_TUTOR_WELCOME }]);
        setInput("");
        setError("");
        setStarting(true);
        (async () => {
            const uinfo = await InvokeAPI(
                `${getAiMgmtBase()}/v1/ai/chat/session/start`,
                {
                    UserID: studentUserId,
                    AssessmentID: assessmentId,
                    QuestionID: questionId,
                },
                "POST",
                { authorization: token },
                false
            );
            if (cancelled) {
                return;
            }
            setStarting(false);
            if (uinfo.unauthenticated === true || uinfo.unauthorized === true) {
                if (onAuthFailure) {
                    onAuthFailure();
                }
                return;
            }
            if (uinfo.iserror === true) {
                setError(uinfo.errorMessage || "Could not start chat session");
                return;
            }
            const sid = uinfo.response?.SessionID;
            if (!sid) {
                setError("Could not start chat session");
                return;
            }
            setSessionId(sid);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, studentUserId, assessmentId, questionId, token, isTrialAccount]);

    const send = async () => {
        const text = input.trim();
        if (!text || !sessionId || sending || isTrialAccount) {
            return;
        }
        setSending(true);
        setInput("");
        setMessages((m) => [...m, { role: "user", text }, { role: "assistant", pending: true }]);
        const uinfo = await InvokeAPI(
            `${getAiMgmtBase()}/v1/ai/chat/message`,
            { SessionID: sessionId, Message: text },
            "POST",
            { authorization: token },
            false
        );
        setSending(false);
        if (uinfo.unauthenticated === true || uinfo.unauthorized === true) {
            setMessages((m) => m.filter((row) => !row.pending));
            if (onAuthFailure) {
                onAuthFailure();
            }
            return;
        }
        if (uinfo.iserror === true) {
            const errText = uinfo.errorMessage || "Message failed";
            setMessages((m) => {
                const withoutPending = m.filter((row) => !row.pending);
                return [...withoutPending, { role: "assistant", error: true, text: errText }];
            });
            return;
        }
        const reply = uinfo.response?.Reply || "";
        setMessages((m) => {
            const withoutPending = m.filter((row) => !row.pending);
            return [...withoutPending, { role: "assistant", text: reply }];
        });
    };

    const questionPlain = plainQuestionText(questionText);
    const showQuestionSubheader =
        (questionNumber != null && questionNumber !== "") || questionPlain;
    const questionPreview =
        questionPlain.length > 50 ? `${questionPlain.slice(0, 50)}...` : questionPlain;

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason === "backdropClick") {
                    return;
                }
                onClose?.();
            }}
            fullWidth
            maxWidth={isExpanded ? false : "sm"}
            fullScreen={isExpanded}
            scroll="paper"
            hideBackdrop={!isExpanded}
            disableScrollLock
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
            slotProps={{
                root: {
                    sx: { pointerEvents: "none" },
                },
            }}
            PaperProps={{
                onWheel: (e) => {
                    e.stopPropagation();
                },
                sx: {
                    pointerEvents: "auto",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    ...(isExpanded
                        ? {
                              height: "100%",
                              maxHeight: "100%",
                              borderRadius: 0,
                          }
                        : {
                              m: 0,
                              position: "fixed",
                              right: 16,
                              bottom: 16,
                              width: { xs: "calc(100vw - 24px)", sm: 460 },
                              maxWidth: "calc(100vw - 24px)",
                              height: { xs: "64vh", sm: 560 },
                              minHeight: 460,
                              maxHeight: "70vh",
                              borderRadius: 2,
                          }),
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 1,
                    pr: 1,
                    pt: 2,
                    pb: 1.5,
                    flexShrink: 0,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography component="span" variant="h6" sx={{ fontSize: "1.05rem", fontWeight: 600, color: "inherit" }}>
                        {`Discussion Question : ${questionNumber != null && questionNumber !== "" ? questionNumber : "-"}`}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                        <IconButton
                            aria-label={isExpanded ? "Collapse chat window" : "Expand chat window"}
                            onClick={() => setIsExpanded((v) => !v)}
                            size="small"
                            sx={{ color: "inherit" }}
                        >
                            {isExpanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                        </IconButton>
                        <IconButton aria-label="close" onClick={onClose} size="small" sx={{ color: "inherit" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                {showQuestionSubheader ? (
                    <Box
                        sx={(theme) => ({
                            borderTop: `1px solid ${alpha(theme.palette.primary.contrastText, 0.28)}`,
                            pt: 1,
                            pr: 0.5,
                        })}
                    >
                        {questionPreview ? (
                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 0,
                                    color: "inherit",
                                    opacity: 0.95,
                                    lineHeight: 1.4,
                                    wordBreak: "break-word",
                                }}
                            >
                                {questionPreview}
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    overscrollBehavior: "contain",
                    py: 1.5,
                }}
            >
                {starting && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={32} />
                    </Box>
                )}
                {!starting && error && (
                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                        {error}
                    </Typography>
                )}
                {!starting && !error && isTrialAccount && (
                    <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>
                        {TRIAL_ACCOUNT_NOTICE}
                    </Typography>
                )}
                {messages.length > 0 && (
                    <Stack spacing={1.5} sx={{ py: 0.5 }}>
                        {messages.map((row, i) => {
                            const isUser = row.role === "user";
                            const isPending = row.pending === true;
                            const isErr = row.error === true;
                            return (
                                <Box
                                    key={i}
                                    sx={{
                                        alignSelf: isUser ? "flex-end" : "flex-start",
                                        maxWidth: "94%",
                                        display: "flex",
                                        flexDirection: isUser ? "row-reverse" : "row",
                                        gap: 1,
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <Avatar
                                        alt=""
                                        aria-label={isUser ? "Your message" : "Tutor message"}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            flexShrink: 0,
                                            mt: 0.25,
                                            bgcolor: isUser ? "primary.main" : "action.selected",
                                            color: isUser ? "primary.contrastText" : "text.secondary",
                                        }}
                                    >
                                        {isUser ? (
                                            <PersonOutlineIcon sx={{ fontSize: 20 }} />
                                        ) : (
                                            <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />
                                        )}
                                    </Avatar>
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 2,
                                            minWidth: 0,
                                            flex: "1 1 auto",
                                            bgcolor: isUser
                                                ? "primary.main"
                                                : isErr
                                                  ? (theme) => alpha(theme.palette.error.main, 0.08)
                                                  : "action.selected",
                                            color: isUser ? "primary.contrastText" : "text.primary",
                                            ...(isErr && {
                                                borderLeft: "3px solid",
                                                borderColor: "error.main",
                                            }),
                                        }}
                                    >
                                        {isUser ? (
                                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                                {row.text}
                                            </Typography>
                                        ) : isPending ? (
                                            <ChatTypingIndicator />
                                        ) : isErr ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ whiteSpace: "pre-wrap", color: "error.dark" }}
                                            >
                                                {row.text}
                                            </Typography>
                                        ) : (
                                            <Box sx={assistantMarkdownSx}>
                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                    {row.text}
                                                </ReactMarkdown>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                        <Box ref={messagesEndRef} sx={{ height: 0, flexShrink: 0 }} aria-hidden />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: 2,
                    pb: 2,
                    pt: 1,
                    flexShrink: 0,
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 1,
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Typography variant="caption" sx={{ alignSelf: "flex-end", color: "text.secondary", mb: -0.5 }}>
                    {input.length}/{MAX_MESSAGE_LENGTH}
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Type your message…"
                    value={input}
                    disabled={isTrialAccount || !sessionId || !!error || starting || sending}
                    onChange={(e) => setInput(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    multiline
                    maxRows={4}
                    inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                />
                <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button onClick={onClose} disabled={sending}>Close</Button>
                    <AsyncButton
                        variant="contained"
                        onClick={send}
                        loading={sending}
                        loadingLabel="Sending…"
                        disabled={isTrialAccount || !sessionId || !!error || starting || sending || !input.trim()}
                    >
                        <SendIcon sx={{ mr: 0.5, verticalAlign: "middle", fontSize: "1.125rem" }} />
                        Send
                    </AsyncButton>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
